/**
 * React only supports error boundaries as a class component, given that we are using functional components
 * we need a way to catch those errors, `withErrorBoundary` is a higher order component that wraps the component
 * into a boundary, and it will catch any error that occurs in the wrapped component tree.
 *
 * Then we can use a hook to catch the error and log them to Sentry!
 */
import type {
  MutableRefObject,
  ComponentType,
  ReactNode,
  PropsWithChildren,
  ReactElement,
  ErrorInfo,
} from 'react';
import {
  Component,
  useState,
  createContext,
  useMemo,
  useRef,
  useCallback,
  useContext,
} from 'react';

export type ComponentDidCatch = (error: unknown, errorInfo: ErrorInfo) => void;

interface ErrorBoundaryCtx {
  componentDidCatch: MutableRefObject<ComponentDidCatch | undefined>;
  error: unknown;
  setError: (error: Error | undefined) => void;
}

interface ErrorBoundaryProps {
  error: unknown;
  onError: ComponentDidCatch;
}

export class ErrorBoundary extends Component<PropsWithChildren<ErrorBoundaryProps>> {
  public static displayName = 'UseErrorBoundary';

  public state: { hasError: boolean } = { hasError: false };

  public static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  public componentDidCatch(...args: Parameters<NonNullable<Component['componentDidCatch']>>): void {
    this.props.onError(...args);
  }

  public render(): React.ReactNode {
    if (this.state.hasError) {
      return null;
    }

    return this.props.children;
  }
}

export const errorBoundaryContext = createContext<ErrorBoundaryCtx>({
  componentDidCatch: { current: undefined },
  error: undefined,
  setError: () => false,
});

export const ErrorBoundaryContext = ({
  children,
}: {
  children?: ReactNode | undefined;
}): ReactNode => {
  const [error, setError] = useState<unknown>();
  const componentDidCatch = useRef<ComponentDidCatch>();
  const ctx = useMemo(
    () => ({
      componentDidCatch,
      error,
      setError,
    }),
    [error],
  );
  return (
    <errorBoundaryContext.Provider value={ctx}>
      <ErrorBoundary
        error={error}
        onError={(newError, errorInfo) => {
          setError(newError);
          componentDidCatch.current?.(newError, errorInfo);
        }}
      >
        {children}
      </ErrorBoundary>
    </errorBoundaryContext.Provider>
  );
};
ErrorBoundaryContext.displayName = 'UseErrorBoundaryContext';

// eslint-disable-next-line func-style
export function withErrorBoundary<Props = Record<string, unknown>>(
  WrappedComponent: ComponentType<Props>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): (props: PropsWithChildren<Props>) => ReactElement<any, any> {
  const WithErrorBoundary = (props: Props) => {
    return (
      <ErrorBoundaryContext>
        <WrappedComponent
          key="WrappedComponent"
          {...props}
        />
      </ErrorBoundaryContext>
    );
  };
  WithErrorBoundary.displayName = `WithErrorBoundary(${
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    WrappedComponent.displayName ?? WrappedComponent.name ?? 'Component'
  })`;

  return WithErrorBoundary;
}

export type UseErrorBoundaryReturn = [error: unknown, resetError: () => void];

export const useErrorBoundary = (componentDidCatch?: ComponentDidCatch): UseErrorBoundaryReturn => {
  const ctx = useContext(errorBoundaryContext);
  ctx.componentDidCatch.current = componentDidCatch;
  const resetError = useCallback(() => {
    ctx.setError(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [ctx.error, resetError];
};
